import React, { FormEvent, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ClipboardCheck,
  LockKeyhole,
  Ticket,
  X,
} from "lucide-react";
import {
  Attendee,
  EventOperationsState,
  RegistrationFormField,
} from "../../types";
import {
  calculateCouponDiscount,
  createEventRegistration,
  getVisibleRegistrationFields,
  resolveTicketType,
} from "../../lib/eventOperations";

interface Props {
  open: boolean;
  state: EventOperationsState;
  onChange: (state: EventOperationsState) => void;
  onClose: () => void;
}

const inputClass =
  "w-full h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-950 focus:outline-none focus:ring-2 focus:ring-emerald-300";
const coreFieldIds = new Set([
  "field-first-name",
  "field-last-name",
  "field-email",
  "field-whatsapp",
  "field-country",
  "field-profession",
  "field-institution",
]);
const coreLabels = new Set([
  "nombre",
  "apellido",
  "email",
  "whatsapp",
  "país",
  "pais",
  "profesión",
  "profesion",
  "institución",
  "institucion",
]);

const DynamicField: React.FC<{
  field: RegistrationFormField;
  value: string | boolean | string[] | undefined;
  onChange: (value: string | boolean | string[]) => void;
}> = ({ field, value, onChange }) => {
  if (field.type === "textarea")
    return (
      <textarea
        value={String(value || "")}
        onChange={(event) => onChange(event.target.value)}
        rows={3}
        placeholder={field.placeholder}
        className={`${inputClass} h-auto py-3`}
      />
    );
  if (field.type === "select")
    return (
      <select
        value={String(value || "")}
        onChange={(event) => onChange(event.target.value)}
        className={inputClass}
      >
        <option value="">Selecciona…</option>
        {field.options?.map((option) => <option key={option}>{option}</option>)}
      </select>
    );
  if (field.type === "checkbox")
    return (
      <label className="h-11 rounded-xl border border-slate-200 bg-white px-3 flex items-center gap-2 text-xs font-bold">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(event) => onChange(event.target.checked)}
        />{" "}
        Sí, confirmo
      </label>
    );
  if (field.type === "radio")
    return (
      <div className="flex flex-wrap gap-2">
        {field.options?.map((option) => (
          <label
            key={option}
            className={`h-10 px-3 rounded-xl border flex items-center gap-2 text-xs font-bold ${value === option ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white"}`}
          >
            <input
              type="radio"
              checked={value === option}
              onChange={() => onChange(option)}
            />
            {option}
          </label>
        ))}
      </div>
    );
  return (
    <input
      type={field.type === "date" ? "date" : "text"}
      value={String(value || "")}
      onChange={(event) => onChange(event.target.value)}
      placeholder={
        field.type === "file"
          ? "URL verificable del archivo"
          : field.placeholder
      }
      className={inputClass}
    />
  );
};

export const EventRegistrationPortalModal: React.FC<Props> = ({
  open,
  state,
  onChange,
  onClose,
}) => {
  const [profession, setProfession] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [ticketTypeId, setTicketTypeId] = useState("");
  const [responses, setResponses] = useState<
    Record<string, string | boolean | string[]>
  >({});
  const [error, setError] = useState("");
  const [registered, setRegistered] = useState<Attendee | null>(null);

  const coupon = useMemo(
    () =>
      state.coupons.find(
        (item) => item.code.toUpperCase() === couponCode.trim().toUpperCase(),
      ),
    [couponCode, state.coupons],
  );
  const tickets = useMemo(
    () =>
      state.ticketTypes.filter((ticket) => {
        const available =
          resolveTicketType(state.ticketTypes, ticket.id)?.id === ticket.id;
        const unlocked =
          ticket.visibility === "public" ||
          ticket.accessCode?.toUpperCase() ===
            couponCode.trim().toUpperCase() ||
          coupon?.ticketTypeId === ticket.id;
        return available && unlocked;
      }),
    [coupon, couponCode, state.ticketTypes],
  );
  const couponHint = useMemo(() => {
    const code = couponCode.trim().toUpperCase();
    if (!code || !ticketTypeId) return null;
    const ticket = state.ticketTypes.find((item) => item.id === ticketTypeId);
    if (!ticket) return null;
    if (ticket.accessCode?.toUpperCase() === code && !coupon)
      return {
        valid: true,
        message: "Código de acceso válido. Ticket oculto habilitado.",
      };
    if (!coupon)
      return {
        valid: false,
        message: "El código no existe o no aplica a una entrada disponible.",
      };
    const result = calculateCouponDiscount(coupon, ticket, ticket.price);
    const total = Math.max(0, ticket.price - result.discount);
    return {
      valid: result.valid,
      message: result.valid
        ? `${result.message} Total: ${total === 0 ? "Gratis" : new Intl.NumberFormat("es-AR", { style: "currency", currency: ticket.currency, maximumFractionDigits: 0 }).format(total)}.`
        : result.message,
    };
  }, [coupon, couponCode, state.ticketTypes, ticketTypeId]);

  useEffect(() => {
    if (!ticketTypeId || !tickets.some((ticket) => ticket.id === ticketTypeId))
      setTicketTypeId(tickets[0]?.id || "");
  }, [ticketTypeId, tickets]);

  useEffect(() => {
    if (!open) {
      setProfession("");
      setCouponCode("");
      setResponses({});
      setError("");
      setRegistered(null);
    }
  }, [open]);

  const visibleFields = useMemo(
    () =>
      getVisibleRegistrationFields(state.registrationFields, {
        ...responses,
        "field-profession": profession,
      }).filter(
        (field) =>
          !coreFieldIds.has(field.id) &&
          !coreLabels.has(field.label.trim().toLowerCase()),
      ),
    [profession, responses, state.registrationFields],
  );

  if (!open) return null;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    const data = new FormData(event.currentTarget);
    const missing = visibleFields.find(
      (field) =>
        field.required &&
        (responses[field.id] === undefined ||
          responses[field.id] === "" ||
          responses[field.id] === false),
    );
    if (missing) {
      setError(`Completa el campo “${missing.label}”.`);
      return;
    }
    const firstName = String(data.get("firstName") || "");
    const lastName = String(data.get("lastName") || "");
    const email = String(data.get("email") || "");
    const result = createEventRegistration(state, {
      firstName,
      lastName,
      email,
      whatsapp: String(data.get("whatsapp") || ""),
      country: String(data.get("country") || ""),
      profession,
      institution: String(data.get("institution") || ""),
      ticketTypeId,
      couponCode,
      formResponses: {
        ...responses,
        "field-first-name": firstName,
        "field-last-name": lastName,
        "field-email": email,
        "field-profession": profession,
      },
      source: "Formulario público",
    });
    if (!result.ok || !result.attendee) {
      setError(result.message);
      return;
    }
    onChange(result.state);
    setRegistered(result.attendee);
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-slate-950/70 backdrop-blur-sm p-3 sm:p-6 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-3xl max-h-[94dvh] bg-slate-50 rounded-3xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col">
        <header className="px-5 py-4 bg-white border-b border-slate-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-2xl bg-emerald-500 flex items-center justify-center">
              <ClipboardCheck size={19} />
            </span>
            <div>
              <p className="text-[9px] font-black uppercase tracking-wider text-emerald-700">
                Formulario público
              </p>
              <h2 className="font-display font-black text-slate-950">
                Inscripción · {state.event.name}
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar registro"
            className="w-9 h-9 rounded-xl border border-slate-200 bg-white flex items-center justify-center"
          >
            <X size={17} />
          </button>
        </header>
        {registered ? (
          <div className="p-6 sm:p-10 overflow-y-auto text-center">
            <span className="w-16 h-16 rounded-3xl bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center">
              <CheckCircle2 size={32} />
            </span>
            <h3 className="font-display text-2xl font-black mt-5">
              Registro recibido
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              {registered.registrationStatus === "registered"
                ? "La inscripción está confirmada."
                : registered.registrationStatus === "under_review"
                  ? "La solicitud quedó en revisión."
                  : "La inscripción fue creada y el pago continúa pendiente."}
            </p>
            <div className="mt-6 max-w-sm mx-auto rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-[9px] font-black uppercase text-slate-400">
                Código único
              </p>
              <p className="font-mono text-lg font-black mt-2">
                {registered.uniqueCode}
              </p>
              <p className="text-[10px] text-slate-500 mt-2">
                El QR se habilita operativamente con este mismo identificador.
              </p>
            </div>
            <button
              onClick={onClose}
              className="mt-6 h-11 px-5 rounded-xl bg-slate-950 text-white text-xs font-black"
            >
              Cerrar
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 overflow-y-auto space-y-5">
            <section className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="font-display font-black text-sm">
                1. Datos del asistente
              </h3>
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                <label className="text-[10px] font-black uppercase text-slate-500">
                  Nombre *
                  <input
                    name="firstName"
                    required
                    className={`${inputClass} mt-1`}
                  />
                </label>
                <label className="text-[10px] font-black uppercase text-slate-500">
                  Apellido *
                  <input
                    name="lastName"
                    required
                    className={`${inputClass} mt-1`}
                  />
                </label>
                <label className="text-[10px] font-black uppercase text-slate-500">
                  Email *
                  <input
                    name="email"
                    type="email"
                    required
                    className={`${inputClass} mt-1`}
                  />
                </label>
                <label className="text-[10px] font-black uppercase text-slate-500">
                  WhatsApp
                  <input name="whatsapp" className={`${inputClass} mt-1`} />
                </label>
                <label className="text-[10px] font-black uppercase text-slate-500">
                  País *
                  <input
                    name="country"
                    required
                    className={`${inputClass} mt-1`}
                  />
                </label>
                <label className="text-[10px] font-black uppercase text-slate-500">
                  Profesión *
                  <select
                    value={profession}
                    onChange={(event) => setProfession(event.target.value)}
                    required
                    className={`${inputClass} mt-1`}
                  >
                    <option value="">Selecciona…</option>
                    <option>Médico</option>
                    <option>Estudiante</option>
                    <option>Consultor</option>
                    <option>Directivo</option>
                    <option>Otro</option>
                  </select>
                </label>
                <label className="text-[10px] font-black uppercase text-slate-500 sm:col-span-2">
                  Institución
                  <input name="institution" className={`${inputClass} mt-1`} />
                </label>
              </div>
            </section>
            {visibleFields.length > 0 && (
              <section className="rounded-2xl border border-slate-200 bg-white p-4">
                <h3 className="font-display font-black text-sm">
                  2. Información adicional
                </h3>
                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  {visibleFields.map((field) => (
                    <label
                      key={field.id}
                      className={`text-[10px] font-black uppercase text-slate-500 ${field.type === "textarea" || field.type === "radio" ? "sm:col-span-2" : ""}`}
                    >
                      {field.label}
                      {field.required ? " *" : ""}
                      <span className="block mt-1">
                        <DynamicField
                          field={field}
                          value={responses[field.id]}
                          onChange={(value) =>
                            setResponses((current) => ({
                              ...current,
                              [field.id]: value,
                            }))
                          }
                        />
                      </span>
                      {field.helpText && (
                        <span className="block text-[9px] normal-case font-medium text-slate-400 mt-1">
                          {field.helpText}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </section>
            )}
            <section className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-display font-black text-sm flex items-center gap-2">
                  <Ticket size={15} className="text-emerald-600" /> 3. Entrada
                </h3>
                <label className="relative w-48">
                  <LockKeyhole
                    size={13}
                    className="absolute left-3 top-3 text-slate-400"
                  />
                  <input
                    value={couponCode}
                    onChange={(event) =>
                      setCouponCode(event.target.value.toUpperCase())
                    }
                    placeholder="Cupón o acceso"
                    className={`${inputClass} h-9 pl-8 text-xs`}
                  />
                </label>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                {tickets.map((ticket) => (
                  <label
                    key={ticket.id}
                    className={`rounded-2xl border p-4 cursor-pointer ${ticketTypeId === ticket.id ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-100" : "border-slate-200 bg-white"}`}
                  >
                    <input
                      type="radio"
                      name="ticket"
                      checked={ticketTypeId === ticket.id}
                      onChange={() => setTicketTypeId(ticket.id)}
                      className="sr-only"
                    />
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-display font-black text-sm">
                          {ticket.name}
                        </p>
                        <p className="text-[10px] text-slate-500 mt-1">
                          {ticket.description}
                        </p>
                      </div>
                      <p className="font-display font-black">
                        {ticket.price === 0
                          ? "Gratis"
                          : new Intl.NumberFormat("es-AR", {
                              style: "currency",
                              currency: ticket.currency,
                              maximumFractionDigits: 0,
                            }).format(ticket.price)}
                      </p>
                    </div>
                    <p className="text-[9px] font-bold text-slate-400 mt-3">
                      {Math.max(0, ticket.capacity - ticket.sold)} cupos
                      disponibles
                      {ticket.requiresApproval ? " · requiere aprobación" : ""}
                    </p>
                  </label>
                ))}
                {tickets.length === 0 && (
                  <p className="sm:col-span-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-bold text-amber-900">
                    No hay entradas disponibles con este código.
                  </p>
                )}
              </div>
            </section>
            {couponHint && (
              <p
                className={`rounded-xl border p-3 text-xs font-bold ${couponHint.valid ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-900"}`}
              >
                {couponHint.message}
              </p>
            )}
            {error && (
              <p className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs font-bold text-rose-800">
                {error}
              </p>
            )}
            <div className="flex items-center justify-between gap-3">
              <p className="text-[10px] text-slate-500 max-w-md">
                Los tickets pagos quedan pendientes hasta que se conecte una
                pasarela real. No se simulan cobros.
              </p>
              <button
                type="submit"
                disabled={!ticketTypeId}
                className="h-11 px-5 rounded-xl bg-emerald-500 disabled:bg-slate-200 disabled:text-slate-400 text-slate-950 text-xs font-black"
              >
                Completar inscripción
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
